/**
 * このファイルの目的
 * DB(organizationTable)とデータをやり取りする（実際にはcacheManageを介してやりとりする）
 *
 *
 * やるべきこと
 * 自組織に関するorganizationTableの最新データを全て取得する
 * 指定された情報を書き込む、削除する
 */

import { getOrganizationId } from '@/appLogic/initializers/organizationId';
import { getOrganizationTableSync } from '@/appLogic/initializers/organizationTable';
import { getBusinessManager } from '@/businessRepository/organizationTable/businessManage';
import { getItemManager } from '@/businessRepository/organizationTable/itemManage';
import { IOrganizationRecord } from '@/coreDomainsModule/entities/record';
import { initProcess } from '@/initProcess';

/**
 * 現状の例:
 * string[]があった場合、
 * string[] => string型にする
 * stringがあった場合、
 * string => never型にする
 * MEMO:
 * Array型の要素の型をもらう。
 * これだとneverを返すのが気持ち悪いので、知っている方がいれば、
 * 1. Array型しか受け付けない
 * 2. Array型の要素の型を取得する
 * を実現してほしいです。
 */
type arrayValue<T> = T extends Array<infer U> ? U : never;

let organizationId = '';
let latestTimestamp = 0;

/**
 *
 * @param id
 */
const setOrganizationId = (id: string) => (organizationId = id); // テストでしか使わない

/**
 * organizationTableの最新情報を取得する
 */
const getAllOrganizationTableData = async () => {
  const table = getOrganizationTableSync();
  if (!organizationId) organizationId = getOrganizationId();
  const allNewRecords = await table.getByTime(organizationId, latestTimestamp, false); // deletedフラグを含むものも含まれている

  // レコードの種類ごとにオブジェクト管理のロジックに渡す
  allNewRecords.forEach((ent) => {
    getBusinessManager().sync(ent);
    getItemManager().sync(ent);
    // getBomManager().sync(ent);
  });
  latestTimestamp = table.getLatestTimestamp(organizationId);
};

/**
 *
 * @param ent
 */
const putInOrganizationTable = async (ent: IOrganizationRecord): Promise<IOrganizationRecord> => {
  const table = getOrganizationTableSync();
  if (!organizationId) organizationId = getOrganizationId();
  ent.organizationId = organizationId;
  return await table.put(ent);
};

/**
 *
 * @param entries
 */
const bulkPutInOrganizationTable = async <T extends Array<object>>(entries: T): Promise<boolean> => {
  const table = getOrganizationTableSync();
  if (!organizationId) organizationId = getOrganizationId();
  entries.forEach((ent: any) => (ent.organizationId = organizationId));
  await table.bulkPut(entries);
  return true;
};

/**
 * 一つのオブジェクトが複数のDBレコードで構成される場合は、同一オブジェクトを構成する全てのレコードをまとめて消さなければならないため、dataIdの配列を渡す
 * @param dataIds {string[]} 消したいレコードのdataId群
 */
const delInOrganizationTable = async (dataIds: string[]) => {
  const table = getOrganizationTableSync();
  if (!organizationId) organizationId = getOrganizationId();
  await table.bulkRemove(dataIds.map((i) => ({ organizationId, dataId: i })));
};

export { setOrganizationId, getAllOrganizationTableData, putInOrganizationTable, bulkPutInOrganizationTable, delInOrganizationTable };
